<template>
  <div>
    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4" />

    <template v-if="loading">
      <v-row class="mb-2">
        <v-col v-for="n in 4" :key="n" cols="6" md="3">
          <v-skeleton-loader type="card" />
        </v-col>
      </v-row>
      <v-skeleton-loader type="image" height="260" class="mb-4" />
      <v-skeleton-loader type="list-item@3" />
    </template>

    <template v-else>
      <p class="text-overline grey--text text--darken-1 mb-1">
        {{ $t("finance_kpi_cards_label") }}
      </p>
      <v-row class="mb-2">
        <v-col v-for="kpi in kpis" :key="kpi.label" cols="6" md="3">
          <v-card outlined class="pa-4 fill-height">
            <div class="text-caption grey--text text--darken-1">{{ kpi.label }}</div>
            <div class="text-h6 font-weight-bold mt-1">{{ kpi.value }}</div>
          </v-card>
        </v-col>
        <v-col v-if="kpis.length === 0" cols="12">
          <empty-state icon="mdi-chart-box-outline" :title="$t('finance_no_kpi_data')" />
        </v-col>
      </v-row>

      <p class="text-overline grey--text text--darken-1 mb-1 mt-4">
        {{ $t("finance_charts_label") }}
      </p>
      <v-row class="mb-2">
        <v-col cols="12" md="7">
          <v-card outlined class="pa-4 fill-height">
            <div class="text-subtitle-2 font-weight-bold">
              {{ $t("finance_turnover_by_day_title") }}
            </div>
            <div class="text-caption grey--text mb-2">
              {{
                $t("finance_last_7_days_currency_hint", { currency: restaurantCurrency })
              }}
            </div>

            <apexchart
              v-if="dailyTurnover.length"
              type="bar"
              height="260"
              :options="dailyTurnoverOptions"
              :series="dailyTurnoverSeries"
            />
            <empty-state v-else icon="mdi-chart-bar" :title="$t('finance_no_data')" />
          </v-card>
        </v-col>

        <v-col cols="12" md="5">
          <v-card outlined class="pa-4 fill-height">
            <div class="text-subtitle-2 font-weight-bold">
              {{ $t("finance_income_vs_costs_title") }}
            </div>
            <div class="text-caption grey--text mb-2">
              {{
                $t("finance_this_month_currency_hint", { currency: restaurantCurrency })
              }}
            </div>

            <div class="d-flex mb-3" style="gap: 16px">
              <span
                class="d-flex align-center text-caption font-weight-medium grey--text text--darken-2"
              >
                <span class="legend-dot income mr-1" /> {{ $t("finance_income") }}
              </span>
              <span
                class="d-flex align-center text-caption font-weight-medium grey--text text--darken-2"
              >
                <span class="legend-dot cost mr-1" /> {{ $t("finance_costs") }}
              </span>
            </div>

            <div v-if="incomeVsCosts.length" class="hbar-list">
              <div v-for="row in incomeVsCosts" :key="row.label" class="hbar-row">
                <span class="hbar-label">{{ row.label }}</span>
                <div class="hbar-track">
                  <div
                    class="hbar-fill"
                    :class="row.kind"
                    :style="{ width: hBarWidth(row.value) }"
                  />
                </div>
                <span class="hbar-value">{{ formatCurrency(row.value) }}</span>
              </div>
            </div>
            <empty-state v-else icon="mdi-scale-balance" :title="$t('finance_no_data')" />
          </v-card>
        </v-col>
      </v-row>

      <v-card outlined class="pa-4">
        <div class="text-subtitle-2 font-weight-bold mb-2">
          {{ $t("finance_recent_activity_title") }}
        </div>

        <empty-state
          v-if="recentActivity.length === 0"
          icon="mdi-history"
          :title="$t('finance_no_recent_activity')"
        />
        <div v-else class="activity-list">
          <div v-for="activity in recentActivity" :key="activity.id" class="activity-row">
            <v-icon size="18" color="success">mdi-check-circle</v-icon>
            <span class="activity-title">{{ activity.title }}</span>
            <span class="activity-time">{{ formatRelativeTime(activity.at) }}</span>
          </div>
        </div>
      </v-card>
    </template>
  </div>
</template>

<script>
import VueApexCharts from "vue-apexcharts";
import EmptyState from "@/components/global/EmptyState.vue";
import hasSnackbar from "@/mixins/hasSnackbar";
import gpsApi from "@/plugins/gpsApi";

export default {
  name: "FinanceTabOverview",

  components: { apexchart: VueApexCharts, EmptyState },

  mixins: [hasSnackbar],

  data: () => ({
    loading: true,
    kpis: [],
    dailyTurnover: [],
    incomeVsCosts: [],
    recentActivity: [],
  }),

  computed: {
    restaurantId() {
      return this.$store.state.restaurant && this.$store.state.restaurant.id;
    },
    maxIncomeVsCosts() {
      return Math.max(1, ...this.incomeVsCosts.map((r) => r.value));
    },
    dailyTurnoverOptions() {
      return {
        chart: { toolbar: { show: false }, fontFamily: "Roboto, sans-serif" },
        plotOptions: { bar: { borderRadius: 4, columnWidth: "55%" } },
        colors: ["#00b37e"],
        dataLabels: { enabled: false },
        xaxis: { categories: this.dailyTurnover.map((p) => p.label) },
        yaxis: { labels: { formatter: (v) => this.formatCurrency(v) } },
        tooltip: { y: { formatter: (v) => this.formatCurrency(v) } },
        grid: { borderColor: "#f1f1f1" },
      };
    },
    dailyTurnoverSeries() {
      return [
        {
          name: this.$t("finance_turnover_series_label"),
          data: this.dailyTurnover.map((p) => p.value),
        },
      ];
    },
  },

  created() {
    this.load();
  },

  methods: {
    async load() {
      if (!this.restaurantId) return;
      this.loading = true;
      try {
        const res = await gpsApi.get(
          `/restaurants/${this.restaurantId}/financial-overview`
        );
        const data = (res.data && res.data.data) || {};
        this.kpis = data.kpis || [];
        this.dailyTurnover = data.daily_turnover || [];
        this.incomeVsCosts = data.income_vs_costs || [];
        this.recentActivity = data.recent_activity || [];
      } catch (error) {
        this.showErrorSnackbar(this.$t("finance_overview_load_error"));
      } finally {
        this.loading = false;
      }
    },
    formatCurrency(value) {
      return `${Number(value || 0).toLocaleString("sr-RS")} ${this.restaurantCurrency}`;
    },
    hBarWidth(value) {
      return `${Math.max(4, (value / this.maxIncomeVsCosts) * 100)}%`;
    },
    formatRelativeTime(iso) {
      if (!iso) return "";
      const diffMs = Date.now() - new Date(iso).getTime();
      const minutes = Math.floor(diffMs / 60000);
      if (minutes < 1) return this.$t("finance_time_just_now");
      if (minutes < 60) return this.$t("finance_time_minutes_ago", { minutes });
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return this.$t("finance_time_hours_ago", { hours });
      const days = Math.floor(hours / 24);
      return this.$t("finance_time_days_ago", { days });
    },
  },
};
</script>

<style scoped>
.legend-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  display: inline-block;
}

.legend-dot.income {
  background: #00b37e;
}

.legend-dot.cost {
  background: #d9820f;
}

.hbar-list {
  display: grid;
  gap: 10px;
}

.hbar-row {
  display: grid;
  grid-template-columns: 78px 1fr auto;
  align-items: center;
  gap: 10px;
}

.hbar-label {
  font-size: 0.8rem;
  color: rgba(0, 0, 0, 0.87);
  font-weight: 600;
}

.hbar-track {
  height: 10px;
  background: #f2f3f7;
  border-radius: 4px;
  overflow: hidden;
}

.hbar-fill {
  height: 100%;
  border-radius: 4px;
}

.hbar-fill.income {
  background: #00b37e;
}

.hbar-fill.cost {
  background: #d9820f;
}

.hbar-value {
  font-size: 0.8rem;
  font-weight: 700;
  white-space: nowrap;
  text-align: right;
}

.activity-list {
  display: grid;
  gap: 2px;
}

.activity-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid #eceef2;
}

.activity-row:last-child {
  border-bottom: none;
}

.activity-title {
  flex: 1;
  font-size: 0.9rem;
  font-weight: 600;
}

.activity-time {
  font-size: 0.78rem;
  color: #9aa4b2;
}
</style>
